import { useState } from 'react';
import { Box, HStack, IconButton, Text } from '@chakra-ui/react';
import { HiClipboardCopy, HiClipboardCheck } from 'react-icons/hi';
import { toaster } from '../../ui/toaster';

export interface CopyableRowProps {
  label: string;
  value: string;
  fieldId: string;
}

export function CopyableRow({ label, value, fieldId }: CopyableRowProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toaster.create({
        title: 'Copied',
        description: `${label} copied to clipboard.`,
        type: 'success',
        duration: 2000,
      });
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toaster.create({
        title: 'Copy failed',
        description: (err as Error).message,
        type: 'error',
        duration: 4000,
      });
    }
  };

  return (
    <Box>
      <Text textStyle="captionEmphasized" color="text.secondary" mb={1}>
        {label}
      </Text>
      <HStack gap={2} align="stretch">
        <Box
          flex={1}
          bg="surface.input"
          borderWidth="1px"
          borderColor="border.secondary"
          borderRadius="md"
          px={3}
          py={2}
          fontFamily="mono"
          fontSize="sm"
          color="text.primary"
          wordBreak="break-all"
          data-e2e={`sso-sp-${fieldId}-value`}
        >
          {value || <Text as="span" color="text.secondary">—</Text>}
        </Box>
        <IconButton
          aria-label={`Copy ${label}`}
          onClick={handleCopy}
          variant="outline"
          size="sm"
          disabled={!value}
          data-e2e={`sso-sp-${fieldId}-copy`}
        >
          {copied ? <HiClipboardCheck /> : <HiClipboardCopy />}
        </IconButton>
      </HStack>
    </Box>
  );
}
